/**
 * One-on-One Match Component
 *
 * 1:1 매치 브라켓을 렌더링하는 컴포넌트
 * 각 매치별로 승/패/워닝/파울 점수를 관리
 */

import React from 'react';
import '@assets/css/comp-form-trmntBracket.css';
import '@assets/css/competition.css';

const CompOneOnOneMatch = ({
    matches,
    theme = 'light',
    onScoreChange,
    onWinnerSelect,
    isFromHome = false
}) => {
    /**
     * 점수 버튼 클릭 핸들러
     */
    const handleScoreClick = (matchIndex, player, scoreType, increment) => {
        if (isFromHome) return; // Home에서 온 경우 수정 불가
        onScoreChange(matchIndex, player, scoreType, increment);
    };

    /**
     * 승자 선택 핸들러
     */
    const handleWinnerClick = (matchIndex, player) => {
        if (isFromHome) return; // Home에서 온 경우 수정 불가
        onWinnerSelect(matchIndex, player);
    };

    /**
     * 점수 버튼 렌더링 (모바일 친화적)
     */
    const renderScoreButton = (matchIndex, player, scoreType, label, color) => {
        const match = matches[matchIndex];
        const currentValue = match[`${player}${scoreType}`] || 0;

        return (
            <div className="d-flex align-items-center justify-content-between mb-2">
                <span className={`badge bg-${color}`} style={{
                    minWidth: '55px',
                    fontSize: '0.75rem',
                    padding: '0.4rem 0.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '40px'
                }}>
                    {label}
                </span>
                <div className="d-flex align-items-center gap-1">
                    <button
                        className={`btn btn-sm btn-outline-${color}`}
                        onClick={() => handleScoreClick(matchIndex, player, scoreType, -1)}
                        disabled={currentValue <= 0 || isFromHome}
                        style={{
                            width: '40px',
                            height: '40px',
                            padding: '0',
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1',
                            fontWeight: 'bold'
                        }}
                    >
                        -
                    </button>
                    <span className="fw-bold" style={{
                        minWidth: '40px',
                        textAlign: 'center',
                        color: theme === 'dark' ? '#e0e0e0' : '#000',
                        fontSize: '1.1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {currentValue}
                    </span>
                    <button
                        className={`btn btn-sm btn-outline-${color}`}
                        onClick={() => handleScoreClick(matchIndex, player, scoreType, 1)}
                        disabled={isFromHome}
                        style={{
                            width: '40px',
                            height: '40px',
                            padding: '0',
                            fontSize: '1.1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1',
                            fontWeight: 'bold'
                        }}
                    >
                        +
                    </button>
                </div>
            </div>
        );
    };

    /**
     * 매치 카드 렌더링 (모바일 최적화)
     */
    const renderMatchCard = (match, matchIndex) => {
        const playerAWins = match.AWins || 0;
        const playerALosses = match.ALosses || 0;
        const playerAWarnings = match.AWarnings || 0;
        const playerAFouls = match.AFouls || 0;

        const playerBWins = match.BWins || 0;
        const playerBLosses = match.BLosses || 0;
        const playerBWarnings = match.BWarnings || 0;
        const playerBFouls = match.BFouls || 0;

        return (
            <div
                key={`match-${matchIndex}`}
                className="card shadow mb-5"
                style={{
                    backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                    border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(0, 0, 0, 0.2)',
                }}
            >
                <div className="card-header" style={{
                    backgroundColor: theme === 'dark' ? '#4a5568' : '#0d6efd',
                    color: '#ffffff',
                    borderBottom: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.4)' : '2px solid rgba(0, 0, 0, 0.2)',
                    padding: '1rem 1.25rem',
                    fontWeight: 'bold'
                }}>
                    <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                        <i className="bi bi-trophy-fill me-2" style={{ color: '#ffd700' }}></i>
                        매치 #{matchIndex + 1}
                    </h5>
                </div>
                <div className="card-body p-2 p-md-3">
                    <div className="row g-2 align-items-center">
                        {/* 선수 A */}
                        <div className="col-12 col-md-5">
                            <div className="p-2 p-md-3 rounded" style={{
                                backgroundColor: match.winner === 'A'
                                    ? (theme === 'dark' ? 'rgba(255, 215, 0, 0.15)' : '#fff9e6')
                                    : (theme === 'dark' ? '#16213e' : '#e8f4f8'),
                                border: match.winner === 'A'
                                    ? (theme === 'dark' ? '2px solid #ffd700' : '2px solid #ffc107')
                                    : (theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid #b8daff'),
                                boxShadow: match.winner === 'A' ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none',
                                transition: 'all 0.3s ease'
                            }}>
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0" style={{
                                        color: theme === 'dark' ? '#e0e0e0' : '#000',
                                        fontSize: '0.95rem'
                                    }}>
                                        <i className="bi bi-person-fill me-1 text-primary"></i>
                                        {match.playerA || '선수 A'}
                                    </h6>
                                    <button
                                        className="btn btn-sm winner-btn"
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            handleWinnerClick(matchIndex, 'A');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        onTouchStart={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onTouchEnd={(e) => {
                                            const target = e.currentTarget;
                                            e.preventDefault();
                                            handleWinnerClick(matchIndex, 'A');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        disabled={isFromHome}
                                        style={{
                                            width: '128px',
                                            fontSize: '0.65rem',
                                            padding: '0.4rem 0.5rem',
                                            fontWeight: 'bold',
                                            transition: 'none',
                                            backgroundColor: match.winner === 'A' ? '#ffc107' : 'transparent',
                                            color: match.winner === 'A' ? '#000' : '#ffc107',
                                            borderColor: '#ffc107',
                                            border: '1px solid #ffc107',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {match.winner === 'A' ? '🏆 WINNER' : 'WINNER'}
                                    </button>
                                </div>
                                <div className="score-controls">
                                    {renderScoreButton(matchIndex, 'A', 'Wins', '승', 'success')}
                                    {renderScoreButton(matchIndex, 'A', 'Losses', '패', 'danger')}
                                    <div style={{
                                        borderTop: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid #dee2e6',
                                        margin: '0.5rem 0'
                                    }}></div>
                                    {renderScoreButton(matchIndex, 'A', 'Warnings', '워닝', 'warning')}
                                    {renderScoreButton(matchIndex, 'A', 'Fouls', '파울', 'secondary')}
                                </div>
                            </div>
                        </div>

                        {/* VS 중앙 배치 */}
                        <div className="col-12 col-md-2 text-center py-2">
                            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '40px' }}>
                                <span
                                    className="badge bg-primary"
                                    style={{
                                        fontSize: '1.1rem',
                                        padding: '0.6rem 1.2rem',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    VS
                                </span>
                            </div>
                        </div>

                        {/* 선수 B */}
                        <div className="col-12 col-md-5">
                            <div className="p-2 p-md-3 rounded" style={{
                                backgroundColor: match.winner === 'B'
                                    ? (theme === 'dark' ? 'rgba(255, 215, 0, 0.15)' : '#fff9e6')
                                    : (theme === 'dark' ? '#16213e' : '#ffe8f0'),
                                border: match.winner === 'B'
                                    ? (theme === 'dark' ? '2px solid #ffd700' : '2px solid #ffc107')
                                    : (theme === 'dark' ? '1px solid rgba(255, 105, 180, 0.3)' : '1px solid #ffb8d4'),
                                boxShadow: match.winner === 'B' ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none',
                                transition: 'all 0.3s ease'
                            }}>
                                <div className="mb-2 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0" style={{
                                        color: theme === 'dark' ? '#e0e0e0' : '#000',
                                        fontSize: '0.95rem'
                                    }}>
                                        <i className="bi bi-person-fill me-1 text-danger"></i>
                                        {match.playerB || '선수 B'}
                                    </h6>
                                    <button
                                        className="btn btn-sm winner-btn"
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            handleWinnerClick(matchIndex, 'B');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        onTouchStart={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onTouchEnd={(e) => {
                                            const target = e.currentTarget;
                                            e.preventDefault();
                                            handleWinnerClick(matchIndex, 'B');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        disabled={isFromHome}
                                        style={{
                                            width: '128px',
                                            fontSize: '0.65rem',
                                            padding: '0.4rem 0.5rem',
                                            fontWeight: 'bold',
                                            transition: 'none',
                                            backgroundColor: match.winner === 'B' ? '#ffc107' : 'transparent',
                                            color: match.winner === 'B' ? '#000' : '#ffc107',
                                            borderColor: '#ffc107',
                                            border: '1px solid #ffc107',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {match.winner === 'B' ? '🏆 WINNER' : 'WINNER'}
                                    </button>
                                </div>
                                <div className="score-controls">
                                    {renderScoreButton(matchIndex, 'B', 'Wins', '승', 'success')}
                                    {renderScoreButton(matchIndex, 'B', 'Losses', '패', 'danger')}
                                    <div style={{
                                        borderTop: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid #dee2e6',
                                        margin: '0.5rem 0'
                                    }}></div>
                                    {renderScoreButton(matchIndex, 'B', 'Warnings', '워닝', 'warning')}
                                    {renderScoreButton(matchIndex, 'B', 'Fouls', '파울', 'secondary')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 점수 요약 */}
                    <div className="mt-3 pt-3" style={{
                        borderTop: theme === 'dark' ? '1px solid rgba(255, 215, 0, 0.2)' : '1px solid rgba(0, 0, 0, 0.125)'
                    }}>
                        <div className="row g-2 text-center">
                            <div className="col-12 col-md-5">
                                <div className="mb-1" style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    color: theme === 'dark' ? '#e0e0e0' : '#495057'
                                }}>
                                    {match.playerA || '선수 A'}
                                </div>
                                <div className="d-flex flex-wrap gap-1 justify-content-center" style={{
                                    color: theme === 'dark' ? '#e0e0e0' : '#495057'
                                }}>
                                    <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>승 {playerAWins}</span>
                                    <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>패 {playerALosses}</span>
                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>워닝 {playerAWarnings}</span>
                                    <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>파울 {playerAFouls}</span>
                                </div>
                            </div>
                            <div className="col-12 col-md-2 d-flex align-items-center justify-content-center">
                                <span style={{ color: theme === 'dark' ? '#a0a0a0' : '#6c757d' }}>|</span>
                            </div>
                            <div className="col-12 col-md-5">
                                <div className="mb-1" style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    color: theme === 'dark' ? '#e0e0e0' : '#495057'
                                }}>
                                    {match.playerB || '선수 B'}
                                </div>
                                <div className="d-flex flex-wrap gap-1 justify-content-center" style={{
                                    color: theme === 'dark' ? '#e0e0e0' : '#495057'
                                }}>
                                    <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>승 {playerBWins}</span>
                                    <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>패 {playerBLosses}</span>
                                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>워닝 {playerBWarnings}</span>
                                    <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>파울 {playerBFouls}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`one-on-one-matches-wrapper ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
            <style>
                {`
                    .winner-btn {
                        -webkit-tap-highlight-color: transparent !important;
                        -webkit-touch-callout: none !important;
                        -webkit-user-select: none !important;
                        user-select: none !important;
                    }
                    .winner-btn:active,
                    .winner-btn:focus,
                    .winner-btn:focus-visible,
                    .winner-btn:focus-within,
                    .winner-btn:hover {
                        outline: none !important;
                        box-shadow: none !important;
                    }
                `}
            </style>
            <div className="section-header rounded mb-4 shadow-sm" style={{
                background: theme === 'dark'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.25rem',
                border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
            }}>
                <h4 className="mb-0 text-white" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    <i className="bi bi-people-fill me-2"></i>
                    1:1 매치
                </h4>
            </div>

            <div className="matches-container">
                {matches.map((match, index) => renderMatchCard(match, index))}
            </div>

            {!isFromHome && (
                <div className="alert alert-info mt-3" style={{ fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    각 선수의 승/패/워닝/파울 점수를 +/- 버튼으로 조정할 수 있습니다.
                </div>
            )}
        </div>
    );
};

export default CompOneOnOneMatch;
